import React, { useState, useEffect, useContext } from 'react';
import { Typography, Button, TextField } from '@mui/material';
import { UserContext } from '../../../context/UserContext';
import API from '../../../config/axiosConfig';
import CommonLayout from '../../../components/CommonLayout';
import DropdownWithIcon from '../../../components/DropdownWithIcon';
import DeleteConfirmationDialog from '../../../components/dialogs/DeleteConfirmationDialog';
import { Skeleton } from '../../../components/Loaders/Skeleton/Skeleton';
import hospitalIcon from '../../../assets/protocols/hospital.svg';
import checklistsData from '../../../apps/NewPreemie/data/checklistsData.json';
import './NewPreemieConfig.scss';

// SVG Icons
const EditIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.0302 9.99899L13.9873 5.95085L16.9095 3.02327L20.951 7.0699L18.0302 9.99899ZM7.62061 20.4325L3.15466 20.8396L3.5507 16.3994L11.9771 7.96215L16.0215 12.0118L7.62061 20.4325ZM23.1082 4.98049L23.1067 4.97899L18.9993 0.86626C17.8876 -0.243787 15.978 -0.29636 14.9248 0.762616L1.42949 14.2754C0.940439 14.7636 0.637408 15.4125 0.574402 16.099L0.00584462 22.3627C-0.0331593 22.8058 0.124357 23.2444 0.439388 23.5599C0.722917 23.8438 1.10546 24 1.49999 24C1.5465 24 1.5915 23.9985 1.63651 23.994L7.89214 23.4247C8.57921 23.3616 9.22577 23.0597 9.71332 22.5715L23.2102 9.05717C24.3023 7.96065 24.2558 6.1311 23.1082 4.98049Z" fill="#0C3058" />
    </svg>
);

const DeleteIcon = () => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.4132 9.42013C15.9082 8.91508 15.0918 8.91508 14.5868 9.42013L12.9167 11.0903L11.2465 9.42013C10.7415 8.91508 9.92517 8.91508 9.42013 9.42013C8.91508 9.92517 8.91508 10.7415 9.42013 11.2465L11.0903 12.9167L9.42013 14.5868C8.91508 15.0918 8.91508 15.9082 9.42013 16.4132C9.672 16.6651 10.0027 16.7917 10.3333 16.7917C10.664 16.7917 10.9947 16.6651 11.2465 16.4132L12.9167 14.7431L14.5868 16.4132C14.8387 16.6651 15.1693 16.7917 15.5 16.7917C15.8307 16.7917 16.1613 16.6651 16.4132 16.4132C16.9183 15.0918 16.9183 15.0918 16.4132 14.5868L14.7431 12.9167L16.4132 11.2465C16.9183 10.7415 16.9183 9.92517 16.4132 9.42013ZM12.9167 23.25C7.21913 23.25 2.58333 18.6142 2.58333 12.9167C2.58333 7.21913 7.21913 2.58333 12.9167 2.58333C18.6142 2.58333 23.25 7.21913 23.25 12.9167C23.25 18.6142 18.6142 23.25 12.9167 23.25ZM12.9167 0C5.79442 0 0 5.79442 0 12.9167C0 20.0389 5.79442 25.8333 12.9167 25.8333C20.0389 25.8333 25.8333 20.0389 25.8333 12.9167C25.8333 5.79442 20.0389 0 12.9167 0Z" fill="#103C6E" />
    </svg>
);

const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
    </svg>
);

const DragIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="9" x2="19" y2="9"></line>
        <line x1="5" y1="15" x2="19" y2="15"></line>
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const ChecklistSkeletonRow = () => (
    <Skeleton>
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f5f5f5',
                borderRadius: 12,
                padding: '12px 16px',
                width: '100%',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                marginBottom: 7,
                minHeight: 60,
            }}
        >
            <div style={{ flex: 1 }}>
                <Skeleton.Line width="70%" height="14px" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
                <Skeleton.Block width="20px" height="20px" />
                <Skeleton.Block width="20px" height="20px" />
            </div>
        </div>
    </Skeleton>
);

// Section name mapping
const sectionNames = {
    medications: 'תרופות',
    roomEquipment: 'ציוד - חדר',
    ventilationEquipment: 'ציוד - הנשמה',
    intubationEquipment: 'ציוד - אינטובציה',
    departmentAdmission: 'קבלה למחלקה',
    parents: 'עדכון הורים'
};

const NewPreemieConfig = () => {
    const { user, hospitals, permittedApps } = useContext(UserContext);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [availableHospitals, setAvailableHospitals] = useState([]);
    const [hospitalsWithEditAccess, setHospitalsWithEditAccess] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Checklists state
    const [checklistsConfig, setChecklistsConfig] = useState(checklistsData);
    const [editingItem, setEditingItem] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [isAddingItem, setIsAddingItem] = useState({ section: null, isAdding: false });
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [selectedSection, setSelectedSection] = useState('medications');
    const [draggedItem, setDraggedItem] = useState(null);

    const APP_NAME = 'NEW_PREEMIE';
    const CONFIG_NAME = 'checklists';

    useEffect(() => {
        if (hospitals && hospitals.length > 0 && user && permittedApps) {
            let editableHospitals = [];

            if (user.isSuperAdmin) {
                editableHospitals = [...hospitals];
            } else {
                const app = permittedApps.find(a => a.key === APP_NAME);
                if (app) {
                    const permittedHospitalIds = app.admins
                        .filter(admin => (admin.user._id === user._id))
                        .map(admin => admin.hospital._id);
                    editableHospitals = hospitals.filter(h => permittedHospitalIds.includes(h._id));
                } else {
                    editableHospitals = [...hospitals];
                }
            }

            setHospitalsWithEditAccess(editableHospitals);

            let hospitalOptions = [...hospitals];
            if (user.isSuperAdmin) {
                hospitalOptions.push({
                    _id: "apps",
                    nameHebrew: "ברירת מחדל",
                    nameEnglish: "apps"
                });
            }
            setAvailableHospitals(hospitalOptions);

            if (editableHospitals.length === 1) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (user.isSuperAdmin && hospitalOptions.length > 0) {
                setSelectedHospital("apps");
            } else if (editableHospitals.length > 0) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (hospitalOptions.length > 0) {
                setSelectedHospital(hospitalOptions[0].nameEnglish);
            }
        }
    }, [hospitals, user, permittedApps]);

    const fetchConfig = async (hospital) => {
        if (!hospital) return;
        try {
            setLoading(true);
            const response = await API.get(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${hospital}`);
            if (response.data.success && response.data.data) {
                setChecklistsConfig(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching config:', error);
            if (error.response && error.response.status === 404) {
                setChecklistsConfig(checklistsData);
                showNotification('נטענו נתוני ברירת מחדל. יש לשמור כדי להחילם.', 'info');
            } else {
                showNotification('שגיאה בטעינת נתונים', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedHospital) {
            fetchConfig(selectedHospital);
        }
    }, [selectedHospital]);

    useEffect(() => {
        if (isAddingItem.isAdding) {
            const element = document.getElementById('add-item-form');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [isAddingItem]);

    const handleHospitalChange = (event) => {
        setSelectedHospital(event.target.value);
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
        setTimeout(() => setNotification(prev => ({ ...prev, open: false })), 3000);
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const saveConfig = async (updatedConfig) => {
        if (!selectedHospital) return;
        try {
            setIsSubmitting(true);
            const response = await API.post(`/api/configs/${APP_NAME}/${CONFIG_NAME}/${selectedHospital}`, {
                data: updatedConfig
            });

            if (response.data.success) {
                setChecklistsConfig(response.data.data);
                showNotification('הנתונים נשמרו בהצלחה');
            }
        } catch (error) {
            console.error('Error saving config:', error);
            showNotification('שגיאה בשמירת הנתונים', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditing = (section, index) => {
        setEditingItem({ section, index });
        setEditingValue(checklistsConfig[section][index].label);
    };

    const cancelEditing = () => {
        setEditingItem(null);
        setEditingValue('');
    };

    const handleSaveEdit = async () => {
        if (!editingValue.trim()) {
            showNotification('יש למלא תיאור הפריט', 'error');
            return;
        }

        const updatedConfig = { ...checklistsConfig };
        updatedConfig[editingItem.section][editingItem.index].label = editingValue.trim();

        await saveConfig(updatedConfig);
        cancelEditing();
    };

    const startAdding = (section) => {
        setIsAddingItem({ section, isAdding: true });
        setEditingValue('');
    };

    const cancelAdding = () => {
        setIsAddingItem({ section: null, isAdding: false });
        setEditingValue('');
    };

    const handleAddItem = async () => {
        if (!editingValue.trim()) {
            showNotification('יש למלא תיאור הפריט', 'error');
            return;
        }

        const section = isAddingItem.section;
        const updatedConfig = { ...checklistsConfig };

        // Ensure the section array exists
        if (!updatedConfig[section]) {
            updatedConfig[section] = [];
        }

        // Generate a unique key based on existing keys
        const existingKeys = updatedConfig[section].map(item => item.key);
        let newKey = editingValue.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_א-ת]/g, '');
        if (!newKey) newKey = 'item';
        let counter = 1;
        let finalKey = newKey;
        while (existingKeys.includes(finalKey)) {
            finalKey = `${newKey}_${counter}`;
            counter++;
        }

        updatedConfig[section].push({
            key: finalKey,
            label: editingValue.trim()
        });

        await saveConfig(updatedConfig);
        cancelAdding();
    };

    const openDeleteConfirm = (section, index) => {
        setDeleteTarget({ section, index });
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (deleteTarget) {
            const updatedConfig = { ...checklistsConfig };
            updatedConfig[deleteTarget.section].splice(deleteTarget.index, 1);

            await saveConfig(updatedConfig);
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
        }
    };

    // Drag and drop handlers
    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        setDraggedItem(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();

        if (draggedItem === null || draggedItem === dropIndex) {
            return;
        }

        const updatedConfig = { ...checklistsConfig };
        const items = [...updatedConfig[selectedSection]];

        // Remove dragged item
        const [removed] = items.splice(draggedItem, 1);

        // Insert at new position
        items.splice(dropIndex, 0, removed);

        updatedConfig[selectedSection] = items;

        await saveConfig(updatedConfig);
        setDraggedItem(null);
    };

    return (
        <CommonLayout>
            <div className="new-preemie-config-container">
                <div className="header-container">
                    <Typography variant="h4" component="h1" className="header-title">
                        ניהול קבלת פג ליחידה
                    </Typography>
                    <a href="/main/apps/new-preemie" rel="noopener noreferrer" className="app-link">
                        לאפליקציה <ArrowLeftIcon />
                    </a>
                </div>

                {(hospitalsWithEditAccess.length > 1 || (user?.isSuperAdmin && hospitals.length > 0)) && (
                    <div>
                        <DropdownWithIcon
                            icon={hospitalIcon}
                            placeholder="בית חולים"
                            value={selectedHospital}
                            onChange={handleHospitalChange}
                            style={{ marginBottom: '24px', marginTop: '24px' }}
                        >
                            {user?.isSuperAdmin ? (
                                availableHospitals.map((hospital) => (
                                    <option key={hospital._id} value={hospital.nameEnglish}>
                                        {hospital.nameHebrew}
                                    </option>
                                ))
                            ) : (
                                hospitalsWithEditAccess.map((hospital) => (
                                    <option key={hospital._id} value={hospital.nameEnglish}>
                                        {hospital.nameHebrew}
                                    </option>
                                ))
                            )}
                        </DropdownWithIcon>
                    </div>
                )}

                {/* Section Tabs */}
                <div className="section-tabs">
                    {Object.keys(sectionNames).map(section => (
                        <button
                            key={section}
                            className={`section-tab ${selectedSection === section ? 'active' : ''}`}
                            onClick={() => setSelectedSection(section)}
                        >
                            {sectionNames[section]}
                        </button>
                    ))}
                </div>

                {/* Checklist Items */}
                <div className="checklists-section">
                    {loading ? (
                        <div className="checklists-list">
                            {Array.from({ length: 5 }).map((_, i) => <ChecklistSkeletonRow key={i} />)}
                        </div>
                    ) : (
                        <>
                            {/* Add Item Button at top */}
                            {selectedHospital && !isAddingItem.isAdding && !editingItem && (
                                <Button
                                    variant="contained"
                                    onClick={() => startAdding(selectedSection)}
                                    startIcon={<PlusIcon />}
                                    sx={{
                                        backgroundColor: '#103C6E',
                                        borderRadius: '8px',
                                        padding: '10px 24px',
                                        marginBottom: '16px',
                                        '&:hover': { backgroundColor: '#0d2e54' }
                                    }}
                                >
                                    הוסף פריט
                                </Button>
                            )}

                            <div className="checklists-list">
                                {checklistsConfig[selectedSection]?.map((item, index) => {
                                    const isEditing = editingItem && editingItem.section === selectedSection && editingItem.index === index;

                                    return (
                                        <div
                                            key={item.key}
                                            className={`checklist-item ${isEditing ? 'editing' : ''} ${draggedItem === index ? 'dragging' : ''}`}
                                            draggable={!isEditing}
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                        >
                                        {isEditing ? (
                                            <div className="edit-form">
                                                <TextField
                                                    fullWidth
                                                    value={editingValue}
                                                    onChange={(e) => setEditingValue(e.target.value)}
                                                    placeholder="תיאור הפריט"
                                                    dir="rtl"
                                                    autoFocus
                                                    sx={{
                                                        marginBottom: '12px',
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                        }
                                                    }}
                                                />
                                                <div className="edit-actions">
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSaveEdit}
                                                        disabled={isSubmitting}
                                                        sx={{
                                                            backgroundColor: '#103C6E',
                                                            borderRadius: '8px',
                                                            padding: '8px 30px',
                                                            '&:hover': { backgroundColor: '#0d2e54' },
                                                            '&:disabled': { backgroundColor: '#ccc' }
                                                        }}
                                                    >
                                                        {isSubmitting ? 'שומר...' : 'שמירת שינויים'}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={cancelEditing}
                                                        sx={{
                                                            borderColor: '#103C6E',
                                                            color: '#103C6E',
                                                            borderRadius: '8px',
                                                            padding: '8px 30px',
                                                            marginRight: '12px'
                                                        }}
                                                    >
                                                        בטל
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="item-header">
                                                <div className="drag-handle" title="גרור לשינוי סדר">
                                                    <DragIcon />
                                                </div>
                                                <div className="item-info">
                                                    <div className="item-label">{item.label}</div>
                                                    {item.category && (
                                                        <div className="item-category">{item.category}</div>
                                                    )}
                                                </div>
                                                <div className="item-actions">
                                                    <div className="action-icon edit-icon" onClick={() => startEditing(selectedSection, index)}>
                                                        <EditIcon />
                                                    </div>
                                                    <div className="action-icon delete-icon" onClick={() => openDeleteConfirm(selectedSection, index)}>
                                                        <DeleteIcon />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {isAddingItem.isAdding && isAddingItem.section === selectedSection && (
                                <div id="add-item-form" className="checklist-item editing">
                                    <div className="edit-form">
                                        <TextField
                                            fullWidth
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            placeholder="תיאור הפריט החדש"
                                            dir="rtl"
                                            autoFocus
                                            sx={{
                                                marginBottom: '12px',
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                }
                                            }}
                                        />
                                        <div className="edit-actions">
                                            <Button
                                                variant="contained"
                                                onClick={handleAddItem}
                                                disabled={isSubmitting}
                                                sx={{
                                                    backgroundColor: '#103C6E',
                                                    borderRadius: '8px',
                                                    padding: '8px 30px',
                                                    '&:hover': { backgroundColor: '#0d2e54' },
                                                    '&:disabled': { backgroundColor: '#ccc' }
                                                }}
                                            >
                                                {isSubmitting ? 'שומר...' : 'הוספה'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={cancelAdding}
                                                sx={{
                                                    borderColor: '#103C6E',
                                                    color: '#103C6E',
                                                    borderRadius: '8px',
                                                    padding: '8px 30px',
                                                    marginRight: '12px'
                                                }}
                                            >
                                                בטל
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            </div>
                        </>
                    )}
                </div>

                <DeleteConfirmationDialog
                    open={deleteConfirmOpen}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={handleDelete}
                    title="האם אתה בטוח שברצונך למחוק את הפריט?"
                    isDeleting={isSubmitting}
                />

                {notification.open && (
                    <div style={{
                        position: 'fixed',
                        bottom: '20px',
                        left: '20px',
                        zIndex: 2000,
                        backgroundColor: notification.severity === 'error' ? '#f44336' : '#4caf50',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div>{notification.message}</div>
                            <button
                                onClick={handleCloseNotification}
                                style={{
                                    marginRight: '10px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </CommonLayout>
    );
};

export default NewPreemieConfig;
